/**
 * extractMergeOutline — token-aware MERGE statement walker. Returns the
 * target + source identifiers, the ON predicate span, and one parsed
 * `WhenBranch` per `WHEN [NOT] MATCHED … THEN <action>` arm.
 *
 * Pure / structural — uses `tokenSkip` so dialect-specific quoting (backticks,
 * brackets, dollar-tags, hash comments) is invisible to brace counting
 * (VQE-RO9 — no regex parsing). Branch / action parsing is split into
 * `parseMergeBranch.ts` to keep this file focused on the statement-shape walker.
 */

import type { ParseOptions, SqlRange } from '../../../../sql-grammar/model';
import type { SkipContext } from '../../../../sql-resolver/classify/tokenSkip';
import { skipContextFor } from '../../../../sql-resolver/classify/tokenSkip';
import {
  consumeParenGroup,
  readIdentifier,
  readWord,
  skipWsAndComments,
} from '../insert/insertParseHelpers';
import {
  findTopLevelWhen,
  parseBranches,
} from './parseMergeBranch';
import type { WhenBranch } from './parseMergeBranch';

export type {
  MergeAction,
  MergeActionKind,
  WhenBranch,
  WhenMatchKind,
} from './parseMergeBranch';

export interface MergeOutline {
  readonly hasMerge: boolean;
  readonly targetText: string | null;
  readonly targetStart: number | null;
  readonly targetEnd: number | null;
  readonly sourceText: string | null;
  readonly sourceStart: number | null;
  readonly sourceEnd: number | null;
  readonly onStart: number | null;
  readonly onEnd: number | null;
  readonly branches: readonly WhenBranch[];
}

const EMPTY_OUTLINE: MergeOutline = {
  hasMerge: false,
  targetText: null, targetStart: null, targetEnd: null,
  sourceText: null, sourceStart: null, sourceEnd: null,
  onStart: null, onEnd: null,
  branches: [],
};

export function extractMergeOutline(
  source: string,
  statementRange: SqlRange,
  options: ParseOptions,
): MergeOutline {
  const start = Math.max(0, statementRange.startOffset);
  const end = Math.max(start, Math.min(source.length, statementRange.endOffset));
  const ctx = skipContextFor(options);

  let i = skipWsAndComments(source, start, end);
  const mergeWord = readWord(source, i, end);
  if (!mergeWord || mergeWord.upper !== 'MERGE') return EMPTY_OUTLINE;
  i = skipWsAndComments(source, mergeWord.end, end);

  const intoWord = readWord(source, i, end);
  if (intoWord && intoWord.upper === 'INTO') {
    i = skipWsAndComments(source, intoWord.end, end);
  }

  const target = readQualifiedTargetWithAlias(source, i, end, ['USING']);
  if (!target) return { ...EMPTY_OUTLINE, hasMerge: true };
  i = skipWsAndComments(source, target.end, end);

  const usingWord = readWord(source, i, end);
  if (!usingWord || usingWord.upper !== 'USING') {
    return {
      ...EMPTY_OUTLINE,
      hasMerge: true,
      targetText: target.text,
      targetStart: target.start,
      targetEnd: target.end,
    };
  }
  i = skipWsAndComments(source, usingWord.end, end);

  const sourceMatch = readSourceWithAlias(source, i, end, ctx);
  if (!sourceMatch) {
    return {
      ...EMPTY_OUTLINE,
      hasMerge: true,
      targetText: target.text,
      targetStart: target.start,
      targetEnd: target.end,
    };
  }
  i = skipWsAndComments(source, sourceMatch.end, end);

  const onWord = readWord(source, i, end);
  if (!onWord || onWord.upper !== 'ON') {
    return {
      ...EMPTY_OUTLINE,
      hasMerge: true,
      targetText: target.text,
      targetStart: target.start,
      targetEnd: target.end,
      sourceText: sourceMatch.text,
      sourceStart: sourceMatch.start,
      sourceEnd: sourceMatch.end,
    };
  }
  const onBodyStart = onWord.end;
  const firstWhen = findTopLevelWhen(source, onBodyStart, end, ctx);
  const onBodyEnd = firstWhen ? firstWhen.start : end;

  const branches = parseBranches(source, firstWhen ? firstWhen.start : end, end, ctx);

  return {
    hasMerge: true,
    targetText: target.text,
    targetStart: target.start,
    targetEnd: target.end,
    sourceText: sourceMatch.text,
    sourceStart: sourceMatch.start,
    sourceEnd: sourceMatch.end,
    onStart: onBodyStart,
    onEnd: onBodyEnd,
    branches,
  };
}

interface IdentifierMatch {
  readonly text: string;
  readonly start: number;
  readonly end: number;
}

const ALIAS_BLOCK_KEYWORDS: ReadonlySet<string> = new Set(['USING', 'ON', 'WHEN', 'AS']);

function readQualifiedTargetWithAlias(
  source: string,
  from: number,
  end: number,
  blockNext: readonly string[],
): IdentifierMatch | null {
  const start = from;
  const first = readIdentifier(source, from, end);
  if (!first) return null;
  let i = first.end;
  while (i < end && source[i] === '.') {
    const next = readIdentifier(source, i + 1, end);
    if (!next) break;
    i = next.end;
  }
  let aliasEnd = i;
  const after = skipWsAndComments(source, i, end);
  const probe = readWord(source, after, end);
  if (probe && probe.upper === 'AS') {
    const ws = skipWsAndComments(source, probe.end, end);
    const aliasIdent = readIdentifier(source, ws, end);
    if (aliasIdent) aliasEnd = aliasIdent.end;
  } else if (probe) {
    const u = probe.upper;
    if (!ALIAS_BLOCK_KEYWORDS.has(u) && !blockNext.includes(u)) {
      aliasEnd = probe.end;
    }
  }
  return { text: source.slice(start, aliasEnd), start, end: aliasEnd };
}

function readSourceWithAlias(
  source: string,
  from: number,
  end: number,
  ctx: SkipContext,
): IdentifierMatch | null {
  const start = from;
  let i = from;
  if (i < end && source[i] === '(') {
    i = consumeParenGroup(source, i, end, ctx);
  } else {
    const first = readIdentifier(source, i, end);
    if (!first) return null;
    i = first.end;
    while (i < end && source[i] === '.') {
      const next = readIdentifier(source, i + 1, end);
      if (!next) break;
      i = next.end;
    }
  }
  let aliasEnd = i;
  const after = skipWsAndComments(source, i, end);
  const probe = readWord(source, after, end);
  if (probe && probe.upper === 'AS') {
    const ws = skipWsAndComments(source, probe.end, end);
    const aliasIdent = readIdentifier(source, ws, end);
    if (aliasIdent) aliasEnd = aliasIdent.end;
  } else if (probe) {
    if (probe.upper !== 'ON' && probe.upper !== 'WHEN') {
      aliasEnd = probe.end;
    }
  }
  return { text: source.slice(start, aliasEnd), start, end: aliasEnd };
}
